/**
 * PubSub Debug Utilities
 * Helper functions for debugging PubSub connection issues
 */

import { storageHelpers } from './localStorage'
import logger from './logger'
import { CONNECTION_STATUS } from '@common'

// Lazy import to avoid circular dependency
const getPubSubService = () => {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    return require('@services/WebPubSub/PubSubService').default
}

/**
 * Get current PubSub connection info for debugging
 * @returns {Object} Connection status and configuration
 */
export const getPubSubInfo = () => {
    const pubSubService = getPubSubService()
    const connectionStatus = pubSubService.getConnectionStatus()
    const connectionError = pubSubService.getConnectionError()
    const negotiateEndpoint = pubSubService.getNegotiateEndpoint()
    const groups = pubSubService.getGroups()
    const reconnectAttempts = pubSubService.reconnectAttempts
    const maxReconnectAttempts = pubSubService.maxReconnectAttempts
    const reconnectInterval = pubSubService.reconnectInterval

    // Get socket state if available
    let socketState = null
    if (pubSubService.socket) {
        const readyState = pubSubService.socket.readyState
        const readyStateText =
            {
                [WebSocket.CONNECTING]: 'CONNECTING',
                [WebSocket.OPEN]: 'OPEN',
                [WebSocket.CLOSING]: 'CLOSING',
                [WebSocket.CLOSED]: 'CLOSED',
            }[readyState] || 'UNKNOWN'

        socketState = {
            readyState,
            readyStateText,
            url: pubSubService.socket.url || null,
            protocol: pubSubService.socket.protocol || null,
        }
    }

    // Note: WebSocket URL is not stored as it expires quickly
    // This is kept for backward compatibility in debug tools only
    let storedConnectionUrl = null
    try {
        // URL may exist from old code, but should not be used for reconnection
        storedConnectionUrl = storageHelpers.getConnectionUrl?.() || null
    } catch (e) {
        // Ignore if method doesn't exist
    }

    // Get desired groups (groups that should be joined)
    const desiredGroups = pubSubService._desiredGroups ? Array.from(pubSubService._desiredGroups) : []

    // Get checkout assistant related info
    const checkoutAssistantInfo = {
        storedCashRegisterId: storageHelpers.getCheckoutAssistantCashRegisterId?.() || null,
        hasStoredId: !!storageHelpers.getCheckoutAssistantCashRegisterId?.(),
    }

    return {
        connectionStatus,
        connectionError:
            connectionError ?
                {
                    message: connectionError.message || String(connectionError),
                    stack: connectionError.stack,
                }
            :   null,
        negotiateEndpoint,
        storedConnectionUrl,
        socketState,
        groups,
        desiredGroups,
        reconnectAttempts,
        maxReconnectAttempts,
        reconnectInterval,
        isConnected: connectionStatus === CONNECTION_STATUS.CONNECTED,
        isConnecting: connectionStatus === CONNECTION_STATUS.CONNECTING,
        hasError: connectionStatus === CONNECTION_STATUS.ERROR,
        listenersCount: pubSubService.listeners?.length || 0,
        connectionStatusListenersCount: pubSubService.connectionStatusListeners?.length || 0,
        groupStatusListenersCount: pubSubService._groupStatusListeners?.length || 0,
        // Checkout assistant specific info
        checkoutAssistant: checkoutAssistantInfo,
        // Summary for checkout assistant status
        checkoutAssistantStatus: {
            isWebSocketConnected: connectionStatus === CONNECTION_STATUS.CONNECTED,
            hasJoinedGroup: groups.length > 0,
            joinedGroupName: groups.length > 0 ? groups[0] : null,
            storedCashRegisterId: checkoutAssistantInfo.storedCashRegisterId,
            expectedGroup:
                checkoutAssistantInfo.storedCashRegisterId ?
                    checkoutAssistantInfo.storedCashRegisterId.startsWith('pos-') ?
                        checkoutAssistantInfo.storedCashRegisterId
                    :   `pos-${checkoutAssistantInfo.storedCashRegisterId}`
                :   null,
            isInExpectedGroup:
                checkoutAssistantInfo.storedCashRegisterId ?
                    groups.includes(
                        checkoutAssistantInfo.storedCashRegisterId.startsWith('pos-') ?
                            checkoutAssistantInfo.storedCashRegisterId
                        :   `pos-${checkoutAssistantInfo.storedCashRegisterId}`
                    )
                :   false,
        },
    }
}

/**
 * Test the negotiate endpoint
 * @returns {Promise<Object>} Negotiate endpoint response
 */
export const testNegotiateEndpoint = async () => {
    const pubSubService = getPubSubService()
    const endpoint = pubSubService.getNegotiateEndpoint()
    if (!endpoint) {
        return {
            success: false,
            error: 'No negotiate endpoint configured',
        }
    }

    try {
        const startTime = Date.now()
        const res = await fetch(endpoint)
        const duration = Date.now() - startTime

        if (!res.ok) {
            return {
                success: false,
                status: res.status,
                statusText: res.statusText,
                duration,
            }
        }

        const data = await res.json()
        return {
            success: true,
            status: res.status,
            url: data.url,
            duration,
            response: data,
        }
    } catch (error) {
        return {
            success: false,
            error: error.message,
            stack: error.stack,
        }
    }
}

/**
 * Manually trigger a connection attempt
 * @returns {Promise<void>}
 */
export const testConnect = async () => {
    try {
        const pubSubService = getPubSubService()
        await pubSubService.connect()
        return {
            success: true,
            message: 'Connection attempt initiated',
        }
    } catch (error) {
        return {
            success: false,
            error: error.message,
            stack: error.stack,
        }
    }
}

/**
 * Get environment variables related to PubSub
 * @returns {Object} Environment configuration
 */
export const getPubSubConfig = () => {
    return {
        negotiateEndpoint: process.env.REACT_APP_NEGOTIATE_ENDPOINT,
        env: process.env.REACT_APP_ENV,
        nodeEnv: process.env.NODE_ENV,
    }
}

/**
 * Force disconnect and clear reconnection attempts
 */
export const forceDisconnect = () => {
    const pubSubService = getPubSubService()
    pubSubService.disconnect()
    pubSubService.reconnectAttempts = 0
    if (pubSubService.reconnectTimeout) {
        clearTimeout(pubSubService.reconnectTimeout)
        pubSubService.reconnectTimeout = null
    }
    logger.debug('PubSub forcefully disconnected')
}

/**
 * Reset reconnection attempts counter
 */
export const resetReconnectAttempts = () => {
    const pubSubService = getPubSubService()
    pubSubService.reconnectAttempts = 0
    logger.debug('Reconnection attempts reset')
}

/**
 * Get checkout assistant specific debug info
 * @returns {Object} Checkout assistant connection status
 */
export const getCheckoutAssistantInfo = () => {
    const pubSubService = getPubSubService()
    const groups = pubSubService.getGroups()
    const connectionStatus = pubSubService.getConnectionStatus()
    const storedCashRegisterId = storageHelpers.getCheckoutAssistantCashRegisterId?.() || null
    // storedCashRegisterId is already in 'pos-{type}-{id}' format, so use it directly
    const expectedGroup = storedCashRegisterId || null
    const joinedGroupName = groups.length > 0 ? groups[0] : null

    return {
        isWebSocketConnected: connectionStatus === CONNECTION_STATUS.CONNECTED,
        hasJoinedGroup: groups.length > 0,
        joinedGroupName,
        storedCashRegisterId,
        expectedGroup,
        isInExpectedGroup: expectedGroup ? groups.includes(expectedGroup) : false,
        allGroups: groups,
        desiredGroups: pubSubService._desiredGroups ? Array.from(pubSubService._desiredGroups) : [],
    }
}

/**
 * Manually join a checkout assistant group
 * @param {string} cashRegisterId - Cash register ID in '{type}-{id}' format or full 'pos-{type}-{id}' format
 * @returns {Promise<Object>} Join result
 */
export const joinCheckoutAssistantGroup = async cashRegisterId => {
    try {
        const pubSubService = getPubSubService()
        // If already has 'pos-' prefix, use it directly; otherwise add the prefix
        const groupName = cashRegisterId.startsWith('pos-') ? cashRegisterId : `pos-${cashRegisterId}`

        // Ensure connected first
        if (pubSubService.getConnectionStatus() !== CONNECTION_STATUS.CONNECTED) {
            await pubSubService.waitUntilConnected(5000)
        }

        // Join the group
        pubSubService.joinGroup(groupName)

        // Wait a bit for the join to complete
        await new Promise(resolve => setTimeout(resolve, 500))

        const groups = pubSubService.getGroups()
        const isJoined = groups.includes(groupName)

        return {
            success: isJoined,
            groupName,
            isJoined,
            allGroups: groups,
            message: isJoined ? `Successfully joined group: ${groupName}` : `Failed to join group: ${groupName}`,
        }
    } catch (error) {
        return {
            success: false,
            error: error.message,
            stack: error.stack,
        }
    }
}

/**
 * Test WebSocket reconnection functionality
 * Simulates a disconnection and monitors reconnection behavior
 * @param {Object} options - Test options
 * @param {Function} options.onStatusChange - Callback when connection status changes
 * @param {Function} options.onComplete - Callback when test completes
 * @param {number} options.timeout - Test timeout in milliseconds (default: 30000)
 * @returns {Promise<Object>} Test result
 */
export const testReconnection = async (options = {}) => {
    const { onStatusChange, onComplete, timeout = 30000 } = options
    const pubSubService = getPubSubService()

    const testResult = {
        success: false,
        initialStatus: null,
        finalStatus: null,
        reconnected: false,
        groupRejoined: false,
        error: null,
        timeline: [],
    }

    let statusListener = null
    const wasAutoReconnectEnabled = pubSubService.getAutoReconnectEnabled()
    try {
        // 1. Get initial status
        const initialInfo = getPubSubInfo()
        testResult.initialStatus = {
            connectionStatus: initialInfo.connectionStatus,
            groups: [...initialInfo.groups],
            socketState: initialInfo.socketState?.readyStateText,
        }
        testResult.timeline.push({
            time: Date.now(),
            event: 'initial',
            status: initialInfo.connectionStatus,
            groups: [...initialInfo.groups],
        })

        if (onStatusChange) {
            onStatusChange('initial', initialInfo.connectionStatus, initialInfo.groups)
        }

        // Check if connected
        if (initialInfo.connectionStatus !== CONNECTION_STATUS.CONNECTED || !pubSubService.socket) {
            throw new Error('WebSocket is not connected. Please ensure you are connected before testing.')
        }

        const storedGroup = storageHelpers.getCheckoutAssistantCashRegisterId?.() || null
        if (!storedGroup) {
            throw new Error('No stored group name found. Please ensure you are connected to a group.')
        }

        // 2. Monitor connection status changes
        let reconnectDetected = false
        // let statusListener = null

        const statusPromise = new Promise((resolve, reject) => {
            statusListener = pubSubService.addConnectionStatusListener((status, error) => {
                const info = getPubSubInfo()
                const timestamp = Date.now()

                testResult.timeline.push({
                    time: timestamp,
                    event: 'status_change',
                    status,
                    error: error ? error.message : null,
                    groups: [...info.groups],
                })

                if (onStatusChange) {
                    onStatusChange('status_change', status, info.groups, error)
                }

                if (status === CONNECTION_STATUS.DISCONNECTED || status === CONNECTION_STATUS.CONNECTING) {
                    reconnectDetected = true
                }

                if (status === CONNECTION_STATUS.CONNECTED && reconnectDetected) {
                    // Reconnection successful
                    testResult.reconnected = true

                    // Wait a bit for groups to be automatically rejoined
                    // (PubSubService._handleOpen has a 100ms delay before rejoining)
                    setTimeout(() => {
                        const finalInfo = getPubSubInfo()
                        testResult.finalStatus = {
                            connectionStatus: finalInfo.connectionStatus,
                            groups: [...finalInfo.groups],
                            socketState: finalInfo.socketState?.readyStateText,
                        }

                        // Check if group was rejoined
                        if (finalInfo.groups.includes(storedGroup)) {
                            testResult.groupRejoined = true
                            testResult.success = true
                        } else {
                            testResult.error = 'Group was not automatically rejoined after reconnection'
                        }

                        resolve(testResult)
                    }, 500) // Wait 500ms to allow groups to rejoin
                } else if (status === CONNECTION_STATUS.ERROR && reconnectDetected) {
                    testResult.error = error ? error.message : 'Connection error occurred'
                    reject(new Error(testResult.error))
                }
            })
        })

        // 3. Reset reconnect attempts to ensure reconnection can happen
        pubSubService.reconnectAttempts = 0

        // Temporarily disable auto-reconnect to have full control over reconnection timing
        // This allows us to simulate a 5-second disconnection period
        pubSubService.setAutoReconnectEnabled(false)
        logger.debug('[Reconnection Test] Temporarily disabled auto-reconnect for testing')

        // Clear any existing reconnect timeout to prevent immediate reconnection
        if (pubSubService.reconnectTimeout) {
            clearTimeout(pubSubService.reconnectTimeout)
            pubSubService.reconnectTimeout = null
            logger.debug('[Reconnection Test] Cleared existing reconnect timeout')
        }

        // 4. Simulate WebSocket disconnection by closing the socket naturally
        // This mimics real network disconnection where the WebSocket close event fires normally
        logger.debug('[Reconnection Test] Simulating WebSocket disconnection...')
        testResult.timeline.push({
            time: Date.now(),
            event: 'disconnect_start',
            message: 'Simulating WebSocket disconnection',
        })

        if (onStatusChange) {
            onStatusChange('disconnect_start', null, null)
        }

        if (!pubSubService.socket) {
            throw new Error('No WebSocket connection to close')
        }

        // Store reference to socket before closing
        const socket = pubSubService.socket
        logger.debug('[Reconnection Test] Closing socket, current state:', socket.readyState)

        // CRITICAL: Remove onmessage listener and wait for event loop to clear any queued messages
        // In real network disconnection, the browser won't fire onmessage after disconnect,
        // but when manually closing, messages might be queued. We need to:
        // 1. Remove the listener to prevent new messages from being processed
        // 2. Wait for the event loop to process any already-queued messages
        // 3. Then close the socket
        socket.onmessage = null
        logger.debug('[Reconnection Test] Removed onmessage listener before closing')

        // Wait for event loop to process any queued messages
        // This ensures that any messages already in the browser's event queue are processed
        // before we close the socket, simulating true disconnection where no new messages arrive
        await new Promise(resolve => {
            // Use setTimeout with 0 delay to let the event loop process any queued messages
            setTimeout(resolve, 0)
        })

        // Close the socket with abnormal closure code to simulate network disconnection
        // Use 3000 (custom code range) to indicate abnormal closure
        // This will trigger _handleClose which will:
        // 1. Update connection status to 'disconnected'
        // 2. Clear groups
        // 3. Remove remaining event listeners
        // 4. Clear socket reference
        // All of this happens naturally through the WebSocket close event, just like real disconnection
        socket.close(3000, 'Simulated network disconnection for testing')

        // Wait a bit for _handleClose to execute (WebSocket close is async)
        await new Promise(resolve => setTimeout(resolve, 200))

        logger.debug('[Reconnection Test] After close:', {
            status: pubSubService.connectionStatus,
            socketState: pubSubService.socket ? pubSubService.socket.readyState : 'null',
            reconnectAttempts: pubSubService.reconnectAttempts,
        })

        testResult.timeline.push({
            time: Date.now(),
            event: 'disconnected', // Using string literal for event name, not status value
            status: pubSubService.connectionStatus,
            message: 'WebSocket disconnected, waiting 5 seconds before reconnection',
        })

        if (onStatusChange) {
            onStatusChange('disconnected', pubSubService.connectionStatus, []) // Using string literal for event name
        }

        // 5. Wait 5 seconds before reconnecting
        // During this period, the WebSocket is disconnected and will not receive any events
        // This is the natural behavior - when socket is closed, onmessage won't fire
        logger.debug('[Reconnection Test] Waiting 5 seconds before reconnection...')
        await new Promise(resolve => setTimeout(resolve, 5000))

        testResult.timeline.push({
            time: Date.now(),
            event: 'reconnect_start',
            message: 'Starting reconnection after 5 second delay',
        })

        if (onStatusChange) {
            onStatusChange('reconnect_start', CONNECTION_STATUS.CONNECTING, [])
        }

        // 6. Re-enable auto-reconnect and trigger reconnection
        logger.debug('[Reconnection Test] Triggering reconnection after 5 second delay')
        pubSubService.setAutoReconnectEnabled(true)
        // Use connect({ ignoreAutoReconnect: true }) to ignore auto-reconnect and force connection even if auto-reconnect was just enabled
        // This ensures reconnection happens immediately
        await pubSubService.connect({ ignoreAutoReconnect: true })

        // 7. Wait for reconnection or timeout
        const timeoutPromise = new Promise((_, timeoutReject) => {
            setTimeout(() => {
                timeoutReject(new Error(`Test timeout after ${timeout}ms`))
            }, timeout)
        })

        await Promise.race([statusPromise, timeoutPromise])

        // Cleanup: Restore auto-reconnect setting
        pubSubService.setAutoReconnectEnabled(wasAutoReconnectEnabled)

        if (statusListener) {
            statusListener()
        }

        if (onComplete) {
            onComplete(testResult)
        }

        return testResult
    } catch (error) {
        testResult.error = error.message
        testResult.success = false

        // Cleanup: Restore auto-reconnect setting even on error
        pubSubService.setAutoReconnectEnabled(wasAutoReconnectEnabled)

        // Cleanup
        if (statusListener) {
            statusListener()
        }

        if (onComplete) {
            onComplete(testResult)
        }

        throw error
    }
}

// Make functions available in browser console for testing
if (typeof window !== 'undefined' && process.env.REACT_APP_ENV === 'dev') {
    window.pubSubDebug = {
        getPubSubInfo,
        testNegotiateEndpoint,
        testConnect,
        getPubSubConfig,
        forceDisconnect,
        resetReconnectAttempts,
        getCheckoutAssistantInfo,
        joinCheckoutAssistantGroup,
        testReconnection,
        // Direct access to service instance (use with caution)
        get service() {
            return getPubSubService()
        },
    }
    logger.debug('PubSub debug utilities available in window.pubSubDebug')
}
